import {
  FRAME_COLORS,
  MATERIAL_TYPES,
  TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import uniq from 'lodash/uniq'
import { translateManager as t } from '../../services/TranslateManager'
import { getSizeName } from '../../services/SizesManager'
import { COUNTRIES } from '../../config/countries-data'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { experimentManager } from '../../services/ExperimentManager/ExperimentManager'
import { readOutsideReaction } from '../../utils/mobx/readOutsideReaction'
import { type Address } from '../../stores/addressStore/address.types'
import { type TilesStore } from '../../stores/tilesStore/TilesStore'
import { type PhotoBooksStore } from '../../stores/photoBooksStore/PhotoBooksStore'
import { type CartStore } from '../../stores/cartStore/CartStore'
import { type ProductToMaterialToSizeToFrame } from '../FrameProvider/AvailableStockProvider.types'

const getIsMaterialSupported = ({
  localSKUs,
  skuProductType,
  material,
}: {
  localSKUs: ProductToMaterialToSizeToFrame
  skuProductType: PRODUCT_TYPES
  material: MATERIAL_TYPES
}) => {
  if (material === MATERIAL_TYPES.FRAMELESS) {
    if (experimentManager.isEnabled('frameless-all-sizes')) {
      return true
    }
    return Object.values(localSKUs?.classic?.classic || {}).some((arr) =>
      arr.includes(FRAME_COLORS.NONE)
    )
  }
  return Object.keys(localSKUs?.[skuProductType]).includes(material)
}

const getIsSizeSupported = ({
  localSKUs,
  skuProductType,
  material,
  tileSize,
}: {
  localSKUs: ProductToMaterialToSizeToFrame
  skuProductType: PRODUCT_TYPES
  material: MATERIAL_TYPES
  tileSize: TILE_SIZES
}) => {
  if (material === MATERIAL_TYPES.FRAMELESS) {
    if (experimentManager.isEnabled('frameless-all-sizes')) {
      return true
    }
    return localSKUs?.classic?.classic?.[tileSize]?.includes(FRAME_COLORS.NONE)
  }
  if (
    material !== MATERIAL_TYPES.CANVAS &&
    tileSize === TILE_SIZES.RECTANGLE_25X8
  ) {
    return localSKUs?.classic?.classic?.[TILE_SIZES.RECTANGLE_25X8]?.includes(
      FRAME_COLORS.NONE
    )
  }
  return Object.keys(localSKUs?.[skuProductType]?.[material])?.includes(
    tileSize
  )
}

// Northern Ireland = UK with a BT postal code
const getIsNorthernIrelandAddress = (
  address: Pick<Address, 'countryId' | 'zipCode'>
) =>
  COUNTRIES[address.countryId]?.code === 'GB' &&
  address.zipCode?.toUpperCase().startsWith('BT')

const getIsPhotoBookSupported = (
  address: Pick<Address, 'countryId' | 'zipCode'>
) => {
  if (getIsNorthernIrelandAddress(address)) {
    return false
  }

  return experimentManager.isEnabled('photo-books')
}

// A row whose book is already gone has nothing left to order.
export const getCartBookStores = (
  cart: CartStore,
  photoBooksStore: PhotoBooksStore
) =>
  readOutsideReaction(() =>
    cart.photoBookItems
      .map((item) => photoBooksStore.booksMap.get(item.id))
      .filter((bookStore) => !!bookStore)
  )

// The bag can hold books and tiles at once, so the page's product type says
// nothing about what the order carries.
export const getUnsupportedBagProduct = ({
  cart,
  photoBooksStore,
  tilesStore,
  address,
  localSKUs,
}: {
  cart: CartStore
  photoBooksStore: PhotoBooksStore
  tilesStore: TilesStore
  address: Pick<Address, 'countryId' | 'zipCode'>
  localSKUs: ProductToMaterialToSizeToFrame | null
}) => {
  const hasBook = getCartBookStores(cart, photoBooksStore).length > 0
  if (hasBook && !getIsPhotoBookSupported(address)) {
    return {
      name: 'photo book',
      material: 'Photo Book',
      size: null,
      type: 'Region',
    }
  }

  const orderedTiles = tilesStore.tiles
  if (orderedTiles.length > 0 && getIsNorthernIrelandAddress(address)) {
    const [firstTile] = orderedTiles
    return {
      type: 'Region',
      material: firstTile.materialType,
      size: firstTile.tileSize,
      name: 'tile',
    }
  }
  const skuProductType = PRODUCT_TYPES.CLASSIC
  if (!localSKUs) {
    // This is null if there is no address
    return null
  }
  const materials = uniq(orderedTiles.map((tile) => tile.materialType))
  for (const material of materials) {
    // Delete after peak
    if (material === MATERIAL_TYPES.CANVAS) {
      continue
    }
    if (
      !Object.keys(localSKUs).includes(skuProductType) ||
      !getIsMaterialSupported({ localSKUs, skuProductType, material })
    ) {
      const materialName = t.get(`order.materials.${material}`).toLowerCase()
      const size = readOutsideReaction(
        () => tilesStore.bulkCustomizationStore.bulkCustomization.tileSize
      )
      return {
        type: 'Material',
        material,
        size,
        name: materialName,
      }
    }
  }

  for (const tile of orderedTiles) {
    // Delete after peak
    if (tile.materialType === MATERIAL_TYPES.CANVAS) {
      continue
    }
    if (
      !getIsSizeSupported({
        localSKUs,
        skuProductType,
        material: tile.materialType,
        tileSize: tile.tileSize,
      })
    ) {
      const sizeName = getSizeName(tile.tileSize).toLowerCase()
      return {
        type: 'Size',
        material: tile.materialType,
        size: tile.tileSize,
        name: sizeName,
      }
    }
  }

  return null
}
